import { ActionV2, ResourceTypeV2 } from '../types/authorizations.types';
import {
  ORDERS_ACTION_CATALOG,
  ordersActionPermission,
} from './orders-actions';
import { TASKS_ACTION_CATALOG, tasksActionPermission } from './tasks-actions';

/**
 * POS manager permissions: the product edits (docs/plans/pos-product-edits.md
 * §3.5) and the approvals that used the shared manager code. Each is an
 * ACTION permission checked on the till against the cached offline
 * credentials, so it works without the backend: a holder signed in goes
 * through, anyone else needs a holder's own PIN. Never rename a value:
 * roles store the permission id, and the name is what the POS checks.
 */
export const POS_ACTIONS = {
  STOCK_COUNT: 'pos:stock.count',
  PURCHASE_QUICK: 'pos:purchase.quick',
  PRICE_CHANGE: 'pos:price.change',
  DISCOUNT_MANUAL: 'pos:discount.manual',
  SALE_CREDIT: 'pos:sale.credit',
  REFUND: 'pos:refund',
  PAYMENT_DEFAULT: 'pos:payment.default',
  SHIFT_OPEN_APPROVE: 'pos:shift.open_approve',
  SHIFT_CLOSE: 'pos:shift.close',
  SHIFT_EXPENSE: 'pos:shift.expense',
} as const;

export type PosAction = (typeof POS_ACTIONS)[keyof typeof POS_ACTIONS];

export interface PosActionDefinition {
  name: PosAction;
  /** English label (the i18n key in the web apps). */
  label: string;
}

export const POS_ACTION_CATALOG: readonly PosActionDefinition[] = [
  { name: POS_ACTIONS.STOCK_COUNT, label: 'Count stock on the POS' },
  {
    name: POS_ACTIONS.PURCHASE_QUICK,
    label: 'Make quick purchase orders on the POS',
  },
  { name: POS_ACTIONS.PRICE_CHANGE, label: 'Change prices on the POS' },
  {
    name: POS_ACTIONS.DISCOUNT_MANUAL,
    label: 'Give manual discounts on the POS',
  },
  { name: POS_ACTIONS.SALE_CREDIT, label: 'Sell on credit on the POS' },
  { name: POS_ACTIONS.REFUND, label: 'Refund sales on the POS' },
  {
    name: POS_ACTIONS.PAYMENT_DEFAULT,
    label: 'Change the default payment type on the POS',
  },
  {
    name: POS_ACTIONS.SHIFT_OPEN_APPROVE,
    label: 'Approve sales made before a shift on the POS',
  },
  {
    name: POS_ACTIONS.SHIFT_CLOSE,
    label: 'Approve closing a shift on the POS',
  },
  {
    name: POS_ACTIONS.SHIFT_EXPENSE,
    label: 'Approve shift expenses on the POS',
  },
];

/** The permission to create for a POS action (`createPermissionV2`). */
export const posActionPermission = (definition: PosActionDefinition) => ({
  resource: { type: ResourceTypeV2.ACTION, name: definition.name },
  action: ActionV2.PERFORM,
  description: `pos: ${definition.label}`,
});

/**
 * Every ACTION permission the deploy sync creates and grants to the
 * Merchant roles (`addOrdersActionsToMerchantRoles.ts`), with the
 * permission it creates.
 */
export const SYNCED_ACTIONS: readonly {
  name: string;
  permission: {
    resource: { type: ResourceTypeV2; name: string };
    action: ActionV2;
    description: string;
  };
}[] = [
  ...ORDERS_ACTION_CATALOG.map((definition) => ({
    name: definition.name,
    permission: ordersActionPermission(definition),
  })),
  ...POS_ACTION_CATALOG.map((definition) => ({
    name: definition.name,
    permission: posActionPermission(definition),
  })),
  ...TASKS_ACTION_CATALOG.map((definition) => ({
    name: definition.name,
    permission: tasksActionPermission(definition),
  })),
];
